import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaWallet,
  FaPaperclip,
  FaEye,
  FaSearch,
  FaTimes,
  FaReceipt,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaFilter,
  FaMoneyBillWave,
  FaCheckCircle,
  FaCalendarAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import ExpenseForm from "../../Components/forms/ExpenseForm";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function ExpenseList() {
  const [expenses, setExpenses] = useState([]);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const [viewingExpense, setViewingExpense] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const { token } = useAuth();

  // Debounce search input to avoid hitting backend on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        pageNo: page,
        pageSize: pageSize,
      };
      if (debouncedSearch && debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }
      if (categoryFilter && categoryFilter !== "All") {
        params.category = categoryFilter;
      }

      const res = await axios.get(`${server}/expenses`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const data = res.data.data || [];
      setExpenses(data);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.totalCount ?? res.data.count ?? data.length);
      if (res.data.totalAmount !== undefined) {
        setTotalAmount(res.data.totalAmount);
      } else {
        setTotalAmount(data.reduce((sum, e) => sum + (e.amount || 0), 0));
      }
    } catch (error) {
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, categoryFilter, debouncedSearch]);

  useEffect(() => {
    if (token) {
      fetchExpenses();
    }
  }, [token, fetchExpenses]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this expense?")) {
      try {
        await axios.delete(`${server}/expenses/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Expense deleted successfully.");
        if (expenses.length === 1 && page > 1) {
          setPage((p) => p - 1);
        } else {
          fetchExpenses();
        }
      } catch (error) {
        toast.error("Error deleting expense");
      }
    }
  };

  const openEditModal = (expense) => {
    setSelectedExpense(expense);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setSelectedExpense(null);
    setIsModalOpen(true);
  };

  const openViewModal = (expense) => {
    setViewingExpense(expense);
    setIsViewModalOpen(true);
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return "N/A";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCategoryBadgeClass = (cat) => {
    const name = (typeof cat === "object" ? cat?.name : cat) || "Other";
    const c = name.toLowerCase();
    if (c.includes("salary"))
      return "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200/60 dark:border-purple-800/40";
    if (c.includes("maint"))
      return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200/60 dark:border-amber-800/40";
    if (c.includes("bill"))
      return "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200/60 dark:border-blue-800/40";
    if (c.includes("event"))
      return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-800/40";
    if (c.includes("suppl"))
      return "bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300 border-teal-200/60 dark:border-teal-800/40";
    if (c.includes("trans"))
      return "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200/60 dark:border-indigo-800/40";
    return "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border-rose-200/60 dark:border-rose-800/40";
  };

  const getPaymentModeBadge = (mode) => {
    const m = (mode || "Cash").toLowerCase();
    if (m.includes("upi"))
      return "bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 border-cyan-200/60 dark:border-cyan-800/40";
    if (m.includes("bank") || m.includes("transfer"))
      return "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200/60 dark:border-blue-800/40";
    if (m.includes("cheque"))
      return "bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 border-violet-200/60 dark:border-violet-800/40";
    return "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-800/40";
  };

  return (
    <div className="p-4 md:p-6 max-w-[1440px] mx-auto min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-4 md:px-6 md:py-4 mb-5 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 bg-gradient-to-br from-rose-500 to-rose-600 text-white rounded-xl shadow-md shadow-rose-500/20 flex items-center justify-center shrink-0">
            <FaWallet size={19} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg md:text-xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
                School Expenditure Register
              </h1>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200/60 dark:border-rose-900/40">
                FY 2026-27
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
              Record and audit vendor payments, bills, payroll, and facility expenditures
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={openCreateModal}
            className="w-full sm:w-auto bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-700 hover:to-rose-800 text-white px-4 py-2 rounded-xl font-bold text-xs transition-all shadow-md shadow-rose-600/20 hover:shadow-lg hover:shadow-rose-600/30 flex items-center justify-center gap-2 cursor-pointer"
          >
            <FaPlus size={10} />
            <span>Add Expense</span>
          </button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mb-5">
        {/* Total Expenditure */}
        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 shadow-xs relative overflow-hidden group hover:border-rose-300 dark:hover:border-rose-800/60 transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Total Expenditure
            </span>
            <div className="w-7 h-7 rounded-lg bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 flex items-center justify-center text-xs">
              <FaWallet />
            </div>
          </div>
          <div className="text-xl md:text-2xl font-black text-rose-600 dark:text-rose-400 tracking-tight">
            ₹{totalAmount.toLocaleString()}
          </div>
          <div className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mt-1">
            Aggregated gross outflow
          </div>
        </div>

        {/* Total Recorded Bills */}
        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 shadow-xs relative overflow-hidden group hover:border-blue-300 dark:hover:border-blue-800/60 transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Recorded Vouchers
            </span>
            <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs">
              <FaReceipt />
            </div>
          </div>
          <div className="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            {totalCount.toLocaleString()}
          </div>
          <div className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mt-1">
            Total ledger entries
          </div>
        </div>

        {/* Average Transaction */}
        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 shadow-xs relative overflow-hidden group hover:border-indigo-300 dark:hover:border-indigo-800/60 transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Average Voucher
            </span>
            <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-xs">
              <FaMoneyBillWave />
            </div>
          </div>
          <div className="text-xl md:text-2xl font-black text-indigo-600 dark:text-indigo-400 tracking-tight">
            ₹
            {totalCount > 0
              ? Math.round(totalAmount / totalCount).toLocaleString()
              : 0}
          </div>
          <div className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mt-1">
            Average per voucher ticket
          </div>
        </div>

        {/* Audit Status */}
        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 shadow-xs relative overflow-hidden group hover:border-emerald-300 dark:hover:border-emerald-800/60 transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Audit Status
            </span>
            <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-xs">
              <FaCheckCircle />
            </div>
          </div>
          <div className="text-base md:text-lg font-black text-emerald-600 dark:text-emerald-400 mt-0.5 truncate">
            {categoryFilter !== "All" ? `${categoryFilter}` : "100% Verified"}
          </div>
          <div className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mt-1">
            {categoryFilter !== "All" ? "Filter applied" : "Audited expenditure records"}
          </div>
        </div>
      </div>

      {/* Main Table Container Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 shadow-xs overflow-hidden mb-6">
        {/* Search & Filter Toolbar */}
        <div className="p-4 border-b border-slate-100 dark:border-slate-800/80 flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-3 bg-slate-50/40 dark:bg-slate-900/40">
          <div className="flex items-center gap-2.5">
            <h2 className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
              Expenditure Vouchers
            </h2>
            <span className="px-2 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-950/70 dark:text-rose-300 rounded-full text-[11px] font-bold">
              {totalCount}
            </span>
          </div>

          {/* Unified Controls Toolbar: Category, Search & PageSize */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5">
            {/* Category Filter */}
            <div className="relative min-w-[140px] sm:w-44">
              <FaFilter className="absolute left-3 top-3 text-slate-400 dark:text-slate-500 text-[10px]" />
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-8 pr-7 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 cursor-pointer transition shadow-2xs"
              >
                <option value="All">All Categories</option>
                <option value="Salary">Salary</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Bill">Bill</option>
                <option value="Event">Event</option>
                <option value="Supplies">Supplies</option>
                <option value="Transportation">Transportation</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Search Input */}
            <div className="relative flex-1 sm:w-64 min-w-[200px]">
              <FaSearch className="absolute left-3 top-3 text-slate-400 dark:text-slate-500 text-xs" />
              <input
                type="text"
                placeholder="Search Title, Vendor, Bill..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-8 pr-8 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition shadow-2xs placeholder:text-slate-400"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setPage(1);
                  }}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 rounded cursor-pointer"
                  title="Clear search"
                >
                  <FaTimes size={10} />
                </button>
              )}
            </div>

            {/* Page Size Selector */}
            <div className="relative min-w-[110px]">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 cursor-pointer transition shadow-2xs"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
          </div>
        </div>

        {/* High-Density Table Display */}
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50/80 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-extrabold text-[10px] uppercase tracking-wider border-b border-slate-200/80 dark:border-slate-800">
                <th className="px-3.5 py-3.5 text-center w-12 font-black">#</th>
                <th className="px-3.5 py-3.5 whitespace-nowrap">Date</th>
                <th className="px-3.5 py-3.5">Expense Title</th>
                <th className="px-3.5 py-3.5">Category</th>
                <th className="px-3.5 py-3.5">Vendor / Paid To</th>
                <th className="px-3.5 py-3.5">Payment Mode</th>
                <th className="px-4 py-3.5 text-right font-black">Amount</th>
                <th className="px-3.5 py-3.5 text-center w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-16 text-rose-600 font-bold">
                    <div className="animate-spin rounded-full h-7 w-7 border-2 border-rose-600 border-t-transparent mx-auto mb-3"></div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold">
                      Loading expenditure records...
                    </span>
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-16 text-slate-400">
                    <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center mx-auto mb-2 text-lg">
                      <FaReceipt />
                    </div>
                    <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      No matching expense records found
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Try adjusting your search criteria or category filter
                    </p>
                  </td>
                </tr>
              ) : (
                expenses.map((exp, idx) => {
                  const serialNo = (page - 1) * pageSize + idx + 1;
                  return (
                    <tr
                      key={exp.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors group"
                    >
                      {/* Serial Number */}
                      <td className="px-3.5 py-3 text-center whitespace-nowrap">
                        <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-mono text-[11px] font-bold">
                          {String(serialNo).padStart(2, "0")}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="px-3.5 py-3 font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <FaCalendarAlt className="text-slate-400 text-[10px]" />
                          {formatDate(exp.date || exp.expenseDate || exp.createdAt)}
                        </span>
                      </td>

                      {/* Expense Title */}
                      <td className="px-3.5 py-3 font-bold text-slate-900 dark:text-white">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate max-w-[220px]" title={exp.title}>
                            {exp.title}
                          </span>
                          {(exp.attachmentUrl || exp.receiptUrl) && (
                            <span
                              className="text-blue-500 hover:text-blue-600 dark:text-blue-400"
                              title="Has Attachment"
                            >
                              <FaPaperclip size={10} />
                            </span>
                          )}
                        </div>
                        {exp.referenceOrReceiptNo && (
                          <div className="text-[10px] font-mono text-slate-400 font-normal">
                            Ref: {exp.referenceOrReceiptNo}
                          </div>
                        )}
                      </td>

                      {/* Category */}
                      <td className="px-3.5 py-3">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-md text-[10px] font-bold border ${getCategoryBadgeClass(
                            exp.category
                          )}`}
                        >
                          {typeof exp.category === "object"
                            ? exp.category?.name || "General"
                            : exp.category || "General"}
                        </span>
                      </td>

                      {/* Vendor / Paid To */}
                      <td className="px-3.5 py-3 font-medium text-slate-700 dark:text-slate-300">
                        {exp.paidTo || "N/A"}
                      </td>

                      {/* Payment Mode */}
                      <td className="px-3.5 py-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold border ${getPaymentModeBadge(
                            exp.paymentMode || exp.paymentMethod
                          )}`}
                        >
                          {exp.paymentMode || exp.paymentMethod || "Cash"}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className="px-4 py-3 text-right font-black text-rose-600 dark:text-rose-400 text-xs whitespace-nowrap">
                        ₹{(exp.amount || 0).toLocaleString()}
                      </td>

                      {/* Actions */}
                      <td className="px-3.5 py-3 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openViewModal(exp)}
                            className="p-1.5 text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                            title="View Voucher"
                          >
                            <FaEye size={11} />
                          </button>
                          {exp.receiptUrl || exp.attachmentUrl ? (
                            <a
                              href={exp.receiptUrl || exp.attachmentUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-950/60 dark:hover:bg-blue-900/60 rounded-lg transition-colors"
                              title="View Document Attachment"
                            >
                              <FaPaperclip size={11} />
                            </a>
                          ) : null}
                          <button
                            onClick={() => openEditModal(exp)}
                            className="p-1.5 text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-950/60 dark:hover:bg-indigo-900/60 rounded-lg transition-colors cursor-pointer"
                            title="Edit Record"
                          >
                            <FaEdit size={11} />
                          </button>
                          <button
                            onClick={() => handleDelete(exp.id)}
                            className="p-1.5 text-rose-600 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 dark:text-rose-400 dark:bg-rose-950/60 dark:hover:bg-rose-900/60 rounded-lg transition-colors cursor-pointer"
                            title="Delete Record"
                          >
                            <FaTrash size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Integrated Pagination Footer */}
        {totalPages > 0 && (
          <div className="p-3.5 px-4 md:px-5 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/60 dark:bg-slate-900/60 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs">
            {/* Range info */}
            <div className="text-slate-500 dark:text-slate-400 font-medium">
              Showing{" "}
              <span className="font-bold text-slate-800 dark:text-slate-200">
                {totalCount === 0 ? 0 : (page - 1) * pageSize + 1}
              </span>{" "}
              to{" "}
              <span className="font-bold text-slate-800 dark:text-slate-200">
                {Math.min(page * pageSize, totalCount)}
              </span>{" "}
              of{" "}
              <span className="font-bold text-slate-800 dark:text-slate-200">
                {totalCount}
              </span>{" "}
              entries
            </div>

            {/* Pagination Button Navigation */}
            <div className="flex items-center gap-1.5">
              {/* First Page */}
              <button
                onClick={() => setPage(1)}
                disabled={page === 1 || loading}
                className="w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700 transition shadow-2xs"
                title="First Page"
              >
                <FaAngleDoubleLeft size={10} />
              </button>

              {/* Prev Page */}
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className="w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700 transition shadow-2xs"
                title="Previous Page"
              >
                <FaChevronLeft size={10} />
              </button>

              {/* Numbered Pills with Ellipsis */}
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === totalPages ||
                      Math.abs(p - page) <= 1
                  )
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "..." ? (
                      <span
                        key={`ellipsis-${i}`}
                        className="text-slate-400 dark:text-slate-500 px-1 select-none text-xs"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        disabled={loading}
                        className={`min-w-[30px] h-8 px-2 rounded-lg text-xs font-bold transition shadow-2xs cursor-pointer ${
                          page === p
                            ? "bg-rose-600 text-white shadow-xs shadow-rose-600/30"
                            : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
              </div>

              {/* Next Page */}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700 transition shadow-2xs"
                title="Next Page"
              >
                <FaChevronRight size={10} />
              </button>

              {/* Last Page */}
              <button
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
                className="w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700 transition shadow-2xs"
                title="Last Page"
              >
                <FaAngleDoubleRight size={10} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Expense Modals */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative w-full max-w-xl"
            >
              <ExpenseForm
                expense={selectedExpense}
                onClose={() => setIsModalOpen(false)}
                onSuccess={() => {
                  setIsModalOpen(false);
                  fetchExpenses();
                }}
              />
            </motion.div>
          </motion.div>
        )}

        {isViewModalOpen && viewingExpense && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full p-6 border border-slate-200 dark:border-slate-800 shadow-2xl space-y-5"
            >
              <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 text-white shadow-lg shadow-rose-500/25">
                    <FaReceipt size={18} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      Voucher Details
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Expense transaction receipt
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                >
                  <FaTimes size={16} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/30 p-4 rounded-xl flex justify-between items-center">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Amount
                    </span>
                    <div className="text-2xl font-black text-rose-600 dark:text-rose-400">
                      ₹{(viewingExpense.amount || 0).toLocaleString()}
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1.5 rounded-full font-bold text-[11px] border ${getCategoryBadgeClass(
                      viewingExpense.category
                    )}`}
                  >
                    {typeof viewingExpense.category === "object"
                      ? viewingExpense.category?.name
                      : viewingExpense.category || "General"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Voucher Title
                    </span>
                    <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                      {viewingExpense.title}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Date
                    </span>
                    <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                      {formatDate(
                        viewingExpense.expenseDate ||
                          viewingExpense.date ||
                          viewingExpense.createdAt
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Payment Mode
                    </span>
                    <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                      {viewingExpense.paymentMethod ||
                        viewingExpense.paymentMode ||
                        "Cash"}
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Paid To / Vendor
                    </span>
                    <div className="font-bold text-slate-900 dark:text-white mt-0.5">
                      {viewingExpense.paidTo || "N/A"}
                    </div>
                  </div>
                </div>

                {viewingExpense.referenceOrReceiptNo && (
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      Receipt / Reference No.
                    </span>
                    <div className="font-mono font-bold text-xs text-slate-700 dark:text-slate-300 mt-0.5">
                      {viewingExpense.referenceOrReceiptNo}
                    </div>
                  </div>
                )}

                {viewingExpense.description && (
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">
                      Notes / Description
                    </span>
                    <div className="text-sm text-slate-700 dark:text-slate-300">
                      {viewingExpense.description}
                    </div>
                  </div>
                )}

                {(viewingExpense.attachmentUrl || viewingExpense.receiptUrl) && (
                  <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-2">
                      Attachment Document
                    </span>
                    {(
                      viewingExpense.attachmentUrl || viewingExpense.receiptUrl
                    ).endsWith(".pdf") ? (
                      <a
                        href={
                          viewingExpense.attachmentUrl ||
                          viewingExpense.receiptUrl
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-950/50 dark:to-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-xl font-bold text-sm hover:shadow-md transition"
                      >
                        <FaPaperclip /> View PDF Document
                      </a>
                    ) : (
                      <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 max-h-48">
                        <img
                          src={
                            viewingExpense.attachmentUrl ||
                            viewingExpense.receiptUrl
                          }
                          alt="Voucher"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-6 py-2.5 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl font-bold text-xs shadow-md transition cursor-pointer"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
